import React from 'react';
import { ArrowLeft, HelpCircle } from 'lucide-react';
import type { DecisionQuestion as DecisionQuestionType } from '../../types/prioritization-selector';

interface DecisionQuestionProps {
  question: DecisionQuestionType;
  value?: string | string[] | number;
  onResponse: (questionId: string, value: string | string[] | number) => void;
  onStartOver: () => void;
}

const DecisionQuestion: React.FC<DecisionQuestionProps> = ({
  question,
  value,
  onResponse,
  onStartOver
}) => {
  const handleOptionSelect = (optionValue: string) => {
    onResponse(question.id, optionValue);
  };

  const handleScaleChange = (scaleValue: number) => {
    onResponse(question.id, scaleValue);
  };

  const handleTextChange = (textValue: string) => {
    onResponse(question.id, textValue);
  };

  const renderQuestionOptions = () => {
    if (!question.options) return null;

    return (
      <div className="space-y-3">
        {question.options.map((option) => (
          <button
            key={option.id}
            onClick={() => handleOptionSelect(option.value)}
            className={`w-full p-4 text-left  border-2 transition-all ${
              value === option.value
                ? 'border-[var(--slate)] bg-[var(--slate-dim)] text-[var(--text-sec)]'
                : 'border-[var(--rule)] hover:border-[var(--rule)] hover:bg-[var(--surface)]'
            }`}
          >
            <div className="flex items-center">
              <div className={`w-4 h-4 rounded-full border-2 mr-3 ${
                value === option.value
                  ? 'border-[var(--slate)] bg-[var(--slate)]'
                  : 'border-[var(--rule)]'
              }`}>
                {value === option.value && (
                  <div className="w-2 h-2 bg-[var(--bg-raised)] rounded-full m-0.5"></div>
                )}
              </div>
              <span className="font-medium">{option.text}</span>
            </div>
          </button>
        ))}
      </div>
    );
  };

  const renderScaleInput = () => {
    return (
      <div className="space-y-4">
        <div className="flex justify-between text-sm text-[var(--text-sec)] mb-2">
          <span>Low</span>
          <span>High</span>
        </div>
        <input
          type="range"
          min="1"
          max="10"
          value={value as number || 5}
          onChange={(e) => handleScaleChange(parseInt(e.target.value))}
          className="w-full h-2 bg-[var(--hover-tint)] appearance-none cursor-pointer slider"
        />
        <div className="text-center">
          <span className="text-2xl font-bold text-[var(--text-ter)]">
            {value as number || 5}
          </span>
        </div>
      </div>
    );
  };

  const renderTextInput = () => {
    return (
      <div>
        <textarea
          value={value as string || ''}
          onChange={(e) => handleTextChange(e.target.value)}
          placeholder="Enter your response..."
          className="w-full p-4 border border-[var(--rule)] focus:ring-2 focus:ring-blue-500 focus:border-[var(--slate)] resize-none"
          rows={4}
        />
      </div>
    );
  };

  const renderInput = () => {
    switch (question.type) {
      case 'single-choice':
      case 'multiple-choice':
        return renderQuestionOptions();
      case 'scale':
        return renderScaleInput();
      case 'text':
        return renderTextInput();
      default:
        return null;
    }
  };

  return (
    <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-8">
      <div className="mb-6">
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-2xl font-display font-bold text-[var(--text-pri)] pr-4">
            {question.text}
          </h2>
          {question.helpText && (
            <div className="group relative">
              <HelpCircle className="w-5 h-5 text-[var(--text-ter)] hover:text-[var(--text-sec)] cursor-help" />
              <div className="absolute right-0 top-6 w-64 p-3 bg-[var(--bg-raised)] text-white text-sm opacity-0 group-hover:opacity-100 transition-opacity z-10">
                {question.helpText}
              </div>
            </div>
          )}
        </div>
        
        {question.required && (
          <p className="text-sm text-[var(--text-sec)] mb-4">
            * This question is required
          </p>
        )}
      </div>

      <div className="mb-8">
        {renderInput()}
      </div>

      <div className="flex justify-between items-center">
        <button
          onClick={onStartOver}
          className="flex items-center text-[var(--text-sec)] hover:text-[var(--text-pri)] font-medium"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Start Over
        </button>
        
        {value && (
          <div className="text-sm text-[var(--text-sec)]">
            Selected: {typeof value === 'string' ? value : value.toString()}
          </div>
        )}
      </div>
    </div>
  );
};

export default DecisionQuestion;

